import { Injectable } from '@angular/core';
import { BaseDAO } from './baseDAO';
import { ApiHelper } from '../apiHelper';
import { ConstantMan } from '../constantMan';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as moment from 'moment';
import { Booking } from 'anchor-shared/lib/model/entity/booking';
import { BrokerCharterGridItem } from '../../../app/portal/broker/broker.component';
import {
  CustomerCharter,
  CustomerSignAgreementData,
} from '../../../app/portal/charters/charter-payment/charter-payment.component';
import { BoatOwnerAvailabilityRequest } from 'anchor-shared/lib/model/interface/boat-owner-availability-request';
import { Payment } from 'anchor-shared/lib/model/entity/payment';
import { CreateLeadFromBuyflowBody } from '../../../app/public/charter-buyflow/charter-buyflow-customer-booking/charter-buyflow-customer-booking.component';
import { CaptainSearchParams } from '../../../app/portal/profile/captain/charter-captains/captain-search/captain-search.component';
import { Captain } from 'anchor-shared/lib/model/entity/captain';
import { AddOn } from 'anchor-shared/lib/model/entity/addOn';

@Injectable()
export class CharterDAO extends BaseDAO<Charter> {
  private PATHS = {
    payBalance: '/payRemainingBalance',
    OPEN_CHARTERS: '/?status=open',
    LEAD_CHARTERS: '/?status=lead',
    COMPLETED_CHARTERS: '/?status=completed',
    CANCELLED_CHARTERS: '/?status=cancelled',
    COMPLETED_BY_VESSEL_ID: '/completed/',
  };

  constructor(public readonly apiHelper: ApiHelper) {
    super(apiHelper, ConstantMan.API.RESOURCE.CHARTER);
  }

  public createCharter(charterData: any) {
    const url: string = this.apiHelper.getServiceName() + this.getResource();
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    const httpRequest: Observable<Booking> = this.apiHelper.apiCall<Booking>(
      url,
      method,
      null,
      charterData,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (charter) => {
          observer.next(charter);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public createLeadFromBuyflow(
    charterData: CreateLeadFromBuyflowBody,
    path = '/user/lead-from-buyflow/create',
  ): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() + this.getResource() + path;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const httpRequest: Observable<Booking> = this.apiHelper.apiCall<Booking>(
      url,
      method,
      null,
      charterData,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (charter) => {
          observer.next(charter);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public initiateBuyflowPaymentRequest(charterId): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/initiate-buyflow-payment/' +
      charterId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<any>(url, method, null, {}, headers);
  }

  public getCharterBaseFee(charterId): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/get-charter-base-fee/' +
      charterId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<any>(url, method, null, {}, headers);
  }

  public createTermCharterLeadFromBuyflow(
    charterData: CreateLeadFromBuyflowBody,
    path = '/user/term-charter-lead-from-buyflow/create',
  ): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() + this.getResource() + path;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const httpRequest: Observable<Booking> = this.apiHelper.apiCall<Booking>(
      url,
      method,
      null,
      charterData,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (charter) => {
          observer.next(charter);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  /**
   * ends up in payment controller because there is no this.getResource()
   */
  sendPaymentRequest(data): Observable<Charter> {
    const url: string = this.apiHelper.getServiceName() + 'payments';
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    const httpRequest: Observable<Charter> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: Charter) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  getStripeTransaction(transactionId: string): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/stripe-transaction-info/' +
      transactionId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<any>(url, method, null, null, headers);
  }

  getOpenChartersWithoutAssignedCaptains(
    searchParams?: CaptainSearchParams,
  ): Observable<{ count: number; rows: Charter[] }> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/assign-captains/get-open-unassigned';
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const body = searchParams;

    return this.apiHelper.apiCall<any>(url, method, null, body, headers);
  }

  getAllOpenChartersWithWaterBodiesWithoutAssignedCaptains(): Observable<
    Charter[]
  > {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/assign-captains/get-waterbodies';
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<any>(url, method, null, null, headers);
  }

  getCharterDetailsWithCaptains(charterId: number): Observable<Charter> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/assign-captains/get-with-captains/' +
      charterId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<any>(url, method, null, null, headers);
  }

  headCaptainAssignCaptainToCharter(
    charterId: number,
    captain: Captain,
  ): Observable<boolean> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/assign-captains/assign/' +
      charterId;
    const method = 'PUT';
    const data = {
      captain: captain,
    };
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  notifyCaptainOfGroup(
    charterId: number,
    captainUserId: number,
  ): Observable<boolean> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/notify-captain/' +
      charterId +
      '/' +
      captainUserId;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<any>(url, method, null, null, headers);
  }

  updatePayment(id: number, data: any) {
    const url: string = this.apiHelper.getServiceName() + 'payments/' + id;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    const httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  getBrokerCharters() {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/broker/grid-items';
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<BrokerCharterGridItem[]>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  loadChartersAfterId(id: number, vesselId: number): Observable<Charter[]> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/after/' +
      id +
      '/vessel/' +
      vesselId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    const httpRequest = this.apiHelper.apiCall<Charter[]>(
      url,
      method,
      null,
      null,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  loadChartersAfterEpoch(epochTime: number): Observable<Charter[]> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/after/epoch/' +
      epochTime;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    const httpRequest = this.apiHelper.apiCall<Charter[]>(
      url,
      method,
      null,
      null,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  loadChartersAfterEpochForVessel(
    epochTime: number,
    vesselId: number,
  ): Observable<Charter[]> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/after/epoch/' +
      epochTime +
      '/' +
      vesselId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    const httpRequest = this.apiHelper.apiCall<Charter[]>(
      url,
      method,
      null,
      null,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public sendRemainingBalanceRequest(id: number): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/' +
      id +
      this.PATHS.payBalance;
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let httpRequest: Observable<Charter> = this.apiHelper.apiCall<Charter>(
      url,
      method,
      null,
      null,
      headers,
      null,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (data: any) => {
          observer.next(data);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public updateCharter(charter: Charter): Observable<Charter> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/' + charter.id;
    let method = 'PUT';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let data: Object = charter;

    let httpRequest: Observable<Charter> = this.apiHelper.apiCall<Charter>(
      url,
      method,
      null,
      data,
      headers,
      null,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (responseData: Charter) => {
          observer.next(responseData);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public createCharterAgreement(charterId: number): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/' +
      charterId +
      '/agreement';
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      null,
      headers,
      null,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (data: Charter) => {
          observer.next(data);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public createCharterAgreementForCustomer(charterId: number): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/' +
      charterId +
      '/customer/agreement';
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      null,
      headers,
      null,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (data: Charter) => {
          observer.next(data);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public customerSignAgreement(charterId: number): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/' +
      charterId +
      '/customer/sign/agreement';
    let method = 'PUT';
    let body = {
      currentDate: moment().format('MM/DD/YYYY'),
    };
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<any>(url, method, null, body, headers, null);
  }

  public loadByStatus(
    status?: string,
    daysToLoad: number = 30,
  ): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `?status=${status}&daysToLoad=${daysToLoad}`;
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<any>(url, method, null, null, headers, null);
  }

  public sendItineraryNotes(_data: any): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/send-itinerary-notes';
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = _data;
    let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      data,
      headers,
      null,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public sendReconciliation(_data: any): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/reconcile-charter';
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = _data;
    let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      data,
      headers,
      null,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public sendUserDepositEmail(_data: any): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/send-user-deposit-email';
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = _data;
    let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      data,
      headers,
      null,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public sendUserFullPaymentEmail(_data: any): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/send-user-full-payment-email';
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = _data;
    let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      data,
      headers,
      null,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public getLeadCharters(): Observable<Charter[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      this.PATHS.LEAD_CHARTERS;
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<any>(url, method, null, null, headers, null);
  }

  public getCompletedByVesselId(vesselId: number): Observable<Charter[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      this.PATHS.COMPLETED_BY_VESSEL_ID +
      vesselId;
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<any>(url, method, null, null, headers, null);
  }

  public loadChartersShallowByVesselId(
    vesselId: number,
  ): Observable<Charter[]> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/shallow/' +
      vesselId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    const httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      data,
      headers,
      null,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (charters: Charter[]) => {
          observer.next(charters);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public getOpenCharters(): Observable<Charter[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      this.PATHS.OPEN_CHARTERS;
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<any>(url, method, null, null, headers, null);
  }

  public getCompletedCharters(): Observable<Charter[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      this.PATHS.COMPLETED_CHARTERS;
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<any>(url, method, null, null, headers, null);
  }

  public getCancelledCharters(): Observable<Charter[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      this.PATHS.CANCELLED_CHARTERS;
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<any>(url, method, null, null, headers, null);
  }

  public updateFromPartial(
    charterId: number,
    charter: Partial<Charter>,
  ): Observable<Charter> {
    const url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/' + charterId;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = charter;
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  public updateAddOns(charterId: number, addOns: AddOn[]): Observable<Charter> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/addOns/' +
      charterId;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = { addOns };
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  public customerCancelCharter(charterId: number): Observable<Charter> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/customer/cancel/' +
      charterId;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  public getSessionTokenByCharterId(
    charterId: number | string,
  ): Observable<any> {
    const url: string = `${this.apiHelper.getServiceName()}${this.getResource()}/session-token/${charterId}`;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<any>(url, method, null, null, headers);
  }

  public customerCancelCharterAlternateTimes(
    charterId: number,
  ): Observable<Charter> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/customer/decline-alt-times/' +
      charterId;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  public customerRequestCaptain(
    captainId: number,
    crewId: number,
    charterId: number,
    newCaptainEmail?: string,
  ): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/customer/request-captain';
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      captainId: captainId,
      crewId: crewId,
      charterId: charterId,
      newCaptainEmail: newCaptainEmail,
    };
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  public customerRequestBoatDelivery(
    charterId: number,
    messageBody: string,
  ): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/customer/request-delivery';
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      messageBody: messageBody,
      charterId: charterId,
    };
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  public getCustomerCharters(): Observable<CustomerCharter[]> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/customers/my-charters';
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<CustomerCharter[]>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  public getCustomerCharterGrid(
    userId: number,
    page: number,
    sortBy?: string,
    sortOrder?: string,
    limit?: number,
  ): Observable<{ count: number; rows: Charter[] }> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/customers/grid?' +
      `userId=${userId}` +
      `&page=${page}` +
      `${sortBy ? '&sortBy=' + sortBy : ''}` +
      `${sortOrder ? '&sortOrder=' + sortOrder : ''}` +
      `${limit ? '&limit=' + limit : ''}`;

    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<{ count: number; rows: Charter[] }>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  public getCustomerCharterDetails(
    charterId: number,
  ): Observable<CustomerCharter> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/customers/charter-details/' +
      charterId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<CustomerCharter>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  public getCustomerCharterDetailsForAgreementSigning(
    charterId: number,
  ): Observable<CustomerSignAgreementData> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/customers/agreement-details/' +
      charterId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<CustomerSignAgreementData>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  public updateExistingChartersForNewUser(userId: number): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/update-existing-charters-new-user/' +
      userId;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<Charter>(url, method, null, data, headers);
  }

  public sendAgreementEmailToCustomer(charterId: number): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/resend-agreement/' +
      charterId;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<Charter>(url, method, null, data, headers);
  }

  public adminSendBoatOwnerText(charterId: number): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/admin/boat-owner-sms/' +
      charterId;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<Charter>(url, method, null, data, headers);
  }

  checkScheduleConflicts(charterId: number, vesselId: number): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/charter-schedule-conflict-check/' +
      vesselId +
      '/' +
      charterId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<any>(url, method, null, null, headers);
  }

  public getPendingForBoatOwnerByCharterId(
    vesselId: number,
    charterId: number,
  ): Observable<BoatOwnerAvailabilityRequest> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/boatowner/request/${vesselId}/${charterId}`;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<BoatOwnerAvailabilityRequest>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  public getPendingForBoatOwner(
    publicId: string,
  ): Observable<BoatOwnerAvailabilityRequest[]> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/boatowner/pending/${publicId}`;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<BoatOwnerAvailabilityRequest[]>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  boatOwnerAvailabilityUpdate(
    charterId: number,
    avail: boolean,
    vesselId?: number,
    unavailableReason?: number,
    userOtherInput?: string,
    boatOwnerPublicId?: string,
  ): Observable<any> {
    const availStatus: string = avail ? 'Available' : 'Not Available';
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/sms-response/${charterId}/${boatOwnerPublicId}`;
    const method = 'POST';
    let body: any = {
      charterId: charterId,
      status: availStatus,
    };
    if (unavailableReason !== undefined) {
      body.unavailableReason = unavailableReason;
      body.vesselId = vesselId;
      if (unavailableReason === 4) {
        body.userOtherInput = userOtherInput;
      }
    }

    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<any>(url, method, null, body, headers);
  }

  refundManualTransaction(
    payment: Payment,
    userEmail: string,
  ): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/refund-manual-payment';
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      payment: payment,
      userEmail: userEmail,
    };
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  sendAdminSMS(message: string, phoneNumber: string): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/send-sms';
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      message: message,
      recipientPhoneNumber: phoneNumber,
    };
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  sendBoatOwnerSMS(
    message: string,
    phoneNumber: string,
    charterId,
    publicId,
  ): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/send-boat-owner-sms/' +
      charterId +
      '/' +
      publicId;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      message: message,
      recipientPhoneNumber: phoneNumber,
    };
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  getAffiliateName(charterId: number): Observable<{ text: string }> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/affiliate/name-text/' +
      charterId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<{ text: string }>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  getBoatOwnerSMSInfo(charterId: number): Observable<BoatOwnerSMSInfo> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/sms-info/' +
      charterId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<BoatOwnerSMSInfo>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  public getChartersByAffiliateId(affiliateId: number): Observable<Charter[]> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/get-charters/' +
      affiliateId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<Charter[]>(url, method, null, null, headers);
  }

  getChartersByCaptainAndDay(captainId: number, charterDate: string) {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/get-charters/' +
      captainId +
      '/' +
      charterDate;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<Charter[]>(url, method, null, null, headers);
  }

  getByIdManageCharter(id: number): Observable<Charter> {
    const url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/manage/' +
      id +
      '/';
    const method = 'GET';
    const headers = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<Charter>(url, method, null, null, headers);
  }

  public getDetailsById(id: any): Observable<Charter> {
    const url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/details/' +
      id +
      '/';
    const method = 'GET';
    const headers = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<Charter>(url, method, null, null, headers);
  }

  public loadGridCharters(
    page: number,
    status: string[],
    searchQuery?: string,
    sortBy?: string,
    sortOrder?: string,
    limit?: number,
  ): Observable<{ count: number; rows: Charter[] }> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/admin/grid/' +
      status.toString() +
      `?page=${page}` +
      `${searchQuery ? '&searchQuery=' + searchQuery : ''}` +
      `${sortBy ? '&sortBy=' + sortBy : ''}` +
      `${sortOrder ? '&sortOrder=' + sortOrder : ''}` +
      `${limit ? '&limit=' + limit : ''}`;
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall(url, method, null, null, headers);
  }

  runSearch(
    page: number,
    searchQuery?: string,
    sortBy?: string,
    sortOrder?: string,
    options?,
  ) {
    return this.loadGridCharters(
      page,
      options.status,
      searchQuery,
      sortBy,
      sortOrder,
      options.limit,
    );
  }

  countOwnerTotalBookings(ownerId: number): Observable<{ count: number }> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/total-bookings/' +
      ownerId;
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<{ count: number }>(
      url,
      method,
      null,
      null,
      headers,
    );
  }
}

export interface CharterPaymentRequest {
  email: string;
  paymentNonce?: string;
  customerToken?: string;
}

export interface BoatOwnerSMSInfo {
  charterId: number;
  boatOwnerTextedTime: Date;
  remainderStatus: string;
  availabilityStatus: string;
  unavailableReason: string;
}
